import store from '@/store';
import { formatPrice, formatTimeAgo, decapitalize } from '@/utils/format.js';
import { getPOIPriceUnitLabel } from '@/utils/poi-detail-labels';
import { isResultMultiPoi } from '@/utils/poi-data/is-multi-poi';
import { isShorttermParking } from '@/utils/poi-data/is-shortterm-parking';
import { getPoiType } from '@/utils/poi-utils';
import { getHtmlDescription } from '@/utils/poi-data/content-descriptions';

export function getPrice(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    let type = poiType
    if(poiType === 'favourites') type = getPoiType(poiType, poiResult);

    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations') {
        return false;
    } else {
        const priceUnformated = getPOIPrice(poiResult, type, multiPoi);
        if (!priceUnformated) return false;
        if(type === 'gasStations') return formatPrice(priceUnformated, true, 3)
        return formatPrice(priceUnformated);
    }
}

export function getPOIPrice(poiResult, poiType, multiPoi = null) {
    if (!poiType) return false;
    if(poiType === 'nearbyStations') return false;
    if (multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    let priceMode = getPriceMode(poiType);

    if(multiPoi) {
        // special exception for fuel
        if(poiResult.type === 'FUEL' && poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.fuel) {
            const prices = poiResult.typeSpecificAttributes.fuel.prices;
            if (!prices || !prices.length || !prices[0].prices) return false;
            const priceArray = prices[0].prices;
            let priceField = getPriceField('gasStations')
            if (!priceMode) return false;
            const priceItem = priceArray.find((price) => price[priceField] === priceMode);
            if (!priceItem) return false;
            return priceItem.price;
        }

        let multiPriceMode;
        let price = false;
        switch(priceMode) {
            case 'PER_HOUR': {
                multiPriceMode = 'pricePerHour';
                break;
            }
            case 'PER_MONTH': {
                multiPriceMode = 'pricePerMonth';
                break;
            }
            case 'PER_DAY': {
                multiPriceMode = 'pricePerDay';
                break;
            }
        }
        if(!multiPriceMode) return false;

        if(poiType === 'carParks' || poiType === 'favourites') {
            if(poiResult.type === 'PARKING') {
                price = poiResult.typeSpecificAttributes.parking ? poiResult.typeSpecificAttributes.parking[multiPriceMode] : false;
            } else if (isShorttermParking(poiType, poiResult)) {
                if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.shorttermparking && poiResult.typeSpecificAttributes.shorttermparking.priceAmount) {
                    price = poiResult.typeSpecificAttributes.shorttermparking.priceAmount;
                }
            }
        }
        return price;
    } else {
        if(!poiResult.data || !poiResult.data.prices) return false
        const prices = poiResult.data.prices;
        if (!prices || !prices.length || !prices[0].prices) return false;
        const priceArray = prices[0].prices;
        let priceField = getPriceField(poiType)
        if (!priceMode) return false;
        const priceItem = priceArray.find((price) => price[priceField] === priceMode);
        if (!priceItem) return false;
        return priceItem.price;
    }
}

export function getMainPrice(poiType, poiResult) {
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites') {
        return false;
    } else {
        const priceList = getPriceList(poiType, poiResult)
        if(!priceList) return false
        for(const i in priceList.sections) {
            const section = priceList.sections[i]
            for(const j in section) {
                const item = section[j]
                if(item.price) {
                    return item
                }
            }
        }
    }
}

export function getPriceList(poiType, poiResult) {
    const formattedPrices = { 
        sections: {
            defaultItems: [],
        },
        comments: [],
        title: 'Preise',
        subtitle: false,
        hideContent: false,
        displayMode: 'accordion',
    };

    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'passRoads') {
        return false;
    } if(poiType === 'chargingStations') {
        formattedPrices.title = 'Ad hoc Laden'
        formattedPrices.displayMode = 'list'
        formattedPrices.subtitle = 'möglich'
        formattedPrices.hideContent = true
        return formattedPrices
    } else {
        if(!poiResult.data || !poiResult.data.prices) return false;
        let prices = false
        if(poiResult.computed.currentPrices) {
            prices = poiResult.computed.currentPrices
        } else if (poiResult.data.prices) {
            prices = {
                prices: []
            }
            poiResult.data.prices.forEach(priceItem => {
                if(priceItem.prices?.length) prices.prices = [...prices.prices, ...priceItem.prices]
            })
        }
        if (!prices || !prices.prices) return false;
        // determine title and style
        if(poiType === 'gasStations') {
            formattedPrices.title = 'Preisübersicht'
            formattedPrices.displayMode = 'list'
        }
        if(poiType === 'sights') {
            formattedPrices.title = 'Eintrittspreise'
            formattedPrices.displayMode = 'list'
        }
        let priceArray = prices.prices;
        let labelKey = 'unit'
        let subLabelKey = false
        let afterComma = 2
        if(poiType === 'gasStations') {
            labelKey = 'fuel'
            afterComma = 3
            // split up prices by fuelSource for gasStations
            if (priceArray.find(item => item.fuelSource === 'ECONTROL')) {
                formattedPrices.sections.defaultItems.push({
                    label: 'Datenquellen',
                    labelSmall: true,
                    infoImage: `${process.env.BASE_URL}/images/logos/econtrol.svg`,
                })
            }
            if (priceArray.find(item => item.fuelSource === 'OEAMTC' || item.fuelSource === 'OTHER')) {
                formattedPrices.sections.otherItems = [{
                    label: 'Andere Quellen',
                    labelSmall: true,
                    // actionLabel: 'Preis melden',
                    actionEvent: 'showPriceChangeEvent'
                }]
            }
        }
        if(poiType === 'sights') {
            labelKey = 'type'
        }
        if(poiType === 'tollRoads') {
            labelKey = 'vehicle' 
            subLabelKey = 'type'
        }
        priceArray = priceArray.sort((a, b) => a.price - b.price);
        if(poiType === 'gasStations') priceArray = priceArray.sort((a, b) => a.fuelSource -  b.fuelSource)
        priceArray.forEach((priceItem) => {
            let itemsKey = 'defaultItems'
            let updated = false
            if(poiType === 'gasStations' && (priceItem.fuelSource === 'OEAMTC' || priceItem.fuelSource === 'OTHER')) itemsKey = 'otherItems'

            if(poiType === 'gasStations' && priceItem.lastUpdated !== undefined) {
                updated = formatTimeAgo(priceItem.lastUpdated) ? `vor ${formatTimeAgo(priceItem.lastUpdated)}` : 'gerade eben'
            }

            if (priceItem.price || priceItem.price === 0) {
                let priceItemLabel = subLabelKey ? priceItem[labelKey][subLabelKey] : priceItem[labelKey]
                formattedPrices.sections[itemsKey].push({
                    price: formatPrice(priceItem.price, true, afterComma, priceItem.currency),
                    label: getPOIPriceUnitLabel(priceItemLabel),
                    updated: updated,
                    type: priceItem[labelKey],
                });
                if(priceItem.comment && priceItem.comment.length && poiType !== 'sights') {
                    formattedPrices.comments = [...formattedPrices.comments, ...getHtmlDescription(priceItem.comment)]
                }
            } else if(!priceItem.price && priceItem.comment?.length) {
                formattedPrices.comments = [...formattedPrices.comments, ...getHtmlDescription(priceItem.comment)]
            } else if(!priceItem.price && poiType === 'gasStations') {
                formattedPrices.sections[itemsKey].push({
                    price: '– , – – –',
                    label: getPOIPriceUnitLabel(priceItem[labelKey]),
                    updated: updated,
                    type: priceItem[labelKey],
                });
            } else if (priceItem.extraChargeWinter) {
                if (!formattedPrices.sections.specialItems)
                    formattedPrices.sections.specialItems = [];
                formattedPrices.sections.specialItems.push({
                    price: `${priceItem.extraChargeWinter} %`,
                    label: 'Winterzuschlag für Dauerparker',
                });
            }
        });
        return formattedPrices;
    }
}

function getPriceMode(type) {
    const poiSettings = store.getters.poiSettings;
    let priceMode = false;
    if(type === 'carParks') {
        priceMode = poiSettings.carParks.priceMode;
    } else if(type === 'gasStations') {
        const queryPoiSettings = store.getters.queryPoiSettings
        priceMode = queryPoiSettings && queryPoiSettings.gasStations && queryPoiSettings.gasStations.fuels ? queryPoiSettings.gasStations.fuels[0] : poiSettings.gasStations.fuels[0];
    }
    return priceMode;
}

function getPriceField(type) {
    let priceField = false;
    switch (type) {
        case 'gasStations':
            priceField = 'fuel'
            break;
        default:
            priceField = 'unit'
            break;
    }
    return priceField;
}

export function getPriceUnit(poiType) {
    let priceMode = getPriceMode(poiType);
    return getPOIPriceUnitLabel(priceMode, true);
}

export function getHourlyPrice(poiType, poiResult) {
    if(isShorttermParking(poiType, poiResult)) {
        const priceList = getPriceList(poiType, poiResult)
        if(!priceList) return false
        for(const i in priceList.sections) {
            const section = priceList.sections[i]
            for(const j in section) {
                const item = section[j]
                if(item.type === 'PER_HOUR') {
                    item.label = decapitalize(item.label)
                    return item
                }
            }
        }
    }
    return false
}